/*
 * Collection Follow Hook using swr-firestore
 * N.B. `data` returns:
 * undefined: when hasn't loaded yet
 * document object: once loaded with data.exists boolean
 * null: when deleted
 */
import { useDocument } from '@nandorojo/swr-firestore';
import * as gtag from '../lib/gtag';
import { useAuth } from '../providers/AuthProvider';

const useCollectionFollow = (collectionAuthorUid, collectionId) => {
  const { authLoaded, uid } = useAuth();

  const {
    data: authIsFollowing,
    error: isFollowingError,
    set: setFollowing,
    deleteDocument: deleteFollowing,
  } = useDocument(
    collectionId && uid
      ? `following/${uid}/collectionFollowing/${collectionId}`
      : null,
    {
      listen: false,
    },
  );

  const follow = () => {
    if (uid) {
      const updatedAt = new Date();
      setFollowing(
        {
          created: updatedAt,
          collectionAuthorUid,
        },
        {
          merge: true,
        },
      );
      gtag.event({
        action: 'collection.follow.new',
        params: {
          uid,
          followedCollectionId: collectionId,
        },
      });
    }
  };

  const unfollow = () => {
    if (uid) {
      deleteFollowing();
      gtag.event({
        action: 'collection.follow.remove',
        params: {
          uid,
          unfollowedCollectionId: collectionId,
        },
      });
    }
  };

  if (!authLoaded) {
    return {
      status: 'waiting',
      error: null,
    };
  }

  if (isFollowingError) {
    return {
      status: 'error',
      error: isFollowingError,
    };
  }

  if (typeof authIsFollowing === 'undefined') {
    return {
      status: 'loading',
      error: null,
    };
  }

  if (authIsFollowing && authIsFollowing.exists) {
    return {
      status: 'following',
      error: null,
      unfollow,
    };
  }

  return {
    status: 'ok',
    error: null,
    follow,
  };
};

export default useCollectionFollow;
